import { supabase } from '../lib/supabase';

interface ErrorWithMessage {
  message: string;
  code?: string;
  details?: any;
}

export async function handleApiError(error: unknown, context: string): Promise<string> {
  const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
  
  // Log error to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error(`API Error in ${context}:`, error);
  }

  // Log error to database
  try {
    await supabase.from('error_logs').insert({
      context,
      error_message: errorMessage,
      error_details: error instanceof Error ? {
        name: error.name,
        stack: error.stack,
        ...(error as ErrorWithMessage).details
      } : String(error),
      timestamp: new Date().toISOString()
    });
  } catch (logError) {
    console.error('Failed to log error:', logError);
  }

  // Return user-friendly error message
  return getUserFriendlyError(error);
}

function getUserFriendlyError(error: unknown): string {
  if (!error) return 'An unknown error occurred';

  // Handle specific error types
  if (error instanceof Error) {
    // Database errors
    if (error.message.includes('duplicate key')) {
      return 'This information already exists in our system';
    }
    if (error.message.includes('validation')) {
      return 'Please check your input and try again';
    }
    if (error.message.includes('timeout')) {
      return 'The request timed out. Please try again';
    }
    
    // Auth errors
    if (error.message.includes('auth')) {
      return 'There was an authentication error. Please try again';
    }

    return error.message;
  }

  return 'An unexpected error occurred. Please try again';
}

export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxAttempts: number = 3,
  delay: number = 1000
): Promise<T> {
  let lastError: Error | undefined;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      if (attempt === maxAttempts) break;
      
      await new Promise(resolve => setTimeout(resolve, delay * attempt));
    }
  }

  throw lastError || new Error('Operation failed after multiple attempts');
}